import { useCallback } from 'react';
import type {
  PropsWithChildren,
  ReactElement,
} from 'react';

import styled from 'styled-components';

import { NavLink } from 'react-router-dom';

import { Counter } from '../Counter';

import {
  MENU_COLLAPSED,
  MENU_HOVER,
} from './constants';
import {
  ITEM_HEIGHT,
  StyledCounter,
  StyledTitleText,
  StyledTitleIcon,
  StyledCounterPoint,
} from './style';

import type {
  SidebarRenderType,
  SidebarLink,
} from './types';

export const StyledWrapper = styled.div<{
  isFirstLevel: boolean;
}>(({
  isFirstLevel,

  theme: {
    sidebar,
  },
}) => ({
  marginBottom: isFirstLevel ? sidebar.FIRST_LEVEL_MARGIN_BOTTOM : 8,

  ':last-child': {
    marginBottom: 0,
  },
}));

export type WrappedNavLinkProps = PropsWithChildren<{
  isHoverRender: boolean;
  isFirstLevel: boolean;
  hasPaddingRight: boolean;
  isActiveItem: boolean;
  isAnchor: boolean;
  url: string;
  target?: string;
  onClick: () => void;
  onMouseEnter: () => void;
}>;

export function WrappedNavLink({
  children = undefined,
  hasPaddingRight,
  isActiveItem,
  isAnchor,
  isFirstLevel,
  isHoverRender,
  target = undefined,
  url,
  ...rest
}: WrappedNavLinkProps): ReactElement {
  if (isAnchor) {
    return (
      <a
        {...rest}
        target={target}
        href={url}
        rel="noreferrer noopener"
      >
        {children}
      </a>
    );
  }

  return (
    <NavLink
      {...rest}
      target={target}
      to={url}
    >
      {children}
    </NavLink>
  );
}

export const StyledTitle = styled(WrappedNavLink)<{
  hasPaddingRight: boolean;
  isHoverRender: boolean;
  isFirstLevel: boolean;
  isActiveItem: boolean;
}>(({
  hasPaddingRight,
  isHoverRender,
  isFirstLevel,
  isActiveItem,

  theme: {
    colors,
    fonts,
    sidebar,
  },
}) => ({
  ...fonts[isFirstLevel ? sidebar.FIRST_LEVEL_FONT : sidebar.DEFAULT_FONT],
  display: 'flex',
  position: 'relative',
  cursor: 'pointer',
  userSelect: 'none',

  fontWeight: isActiveItem ? sidebar.ACTIVE_TITLE_FONT_WEIGHT : 400,

  color: isActiveItem
    ? colors[sidebar.TITLE_FIRST_LEVEL_COLOR]
    : isFirstLevel
      ? colors[sidebar.TITLE_FIRST_LEVEL_COLOR]
      : colors[sidebar.TITLE_COLOR],

  paddingRight: (isHoverRender || hasPaddingRight) ? 12 : 0,
  minHeight: isFirstLevel ? ITEM_HEIGHT : undefined,
  outline: 'none',
  marginLeft: isFirstLevel ? undefined : -8,
  paddingLeft: (isFirstLevel && !isHoverRender) ? undefined : 8,

  backgroundColor: isActiveItem
    ? isFirstLevel
      ? colors[sidebar.ACTIVE_FIRST_LEVEL_LINK_BACKGROUND_COLOR]
      : colors[sidebar.ACTIVE_GROUP_LINK_BACKGROUND_COLOR]
    : isFirstLevel
      ? sidebar.INACTIVE_GROUP_BACKGROUND_COLOR
        ? colors[sidebar.INACTIVE_GROUP_BACKGROUND_COLOR]
        : 'transparent'
      : undefined,

  '&::before': {
    content: (isActiveItem && isFirstLevel) ? '""' : undefined,
    position: 'absolute',
    top: 0,
    left: 0,
    width: sidebar.BEFORE_TITLE_WIDTH,
    height: sidebar.BEFORE_TITLE_HEIGHT,
    backgroundColor: colors[sidebar.BEFORE_TITLE_BACKGROUND_COLOR],
  },

  ':hover': {
    color: colors[sidebar.TITLE_FIRST_LEVEL_COLOR],
    backgroundColor: isFirstLevel
      ? colors[sidebar.TITLE_HOVER_FIRST_LEVEL_BACKGROUND_COLOR]
      : colors[sidebar.TITLE_HOVER_BACKGROUND_COLOR],

    '&::before': isFirstLevel && sidebar.BEFORE_TITLE_SHOW_ON_HOVER ? {
      content: '""',
    } : undefined,
  },

  ':active': {
    color: colors[sidebar.TITLE_FIRST_LEVEL_COLOR],
    backgroundColor: isFirstLevel
      ? colors[sidebar.TITLE_FOCUS_FIRST_LEVEL_BACKGROUND_COLOR]
      : colors[sidebar.TITLE_FOCUS_BACKGROUND_COLOR],

    '&::before': isFirstLevel && sidebar.BEFORE_TITLE_SHOW_ON_FOCUS ? {
      content: '""',
    } : undefined,
  },
}));

export type LinkWrapperProps = {
  level: number;
  link: SidebarLink;
  counters: {
    [key: string]: number | { count: number; isError?: boolean };
  };
  countersMaxNumber?: number | null;

  renderType: SidebarRenderType;

  isActiveItem: boolean;

  index: number;
  closeMenu: () => void;

  setHoverGroupIndex?: (hoveGroupIndex: number) => void;
};

const renderCounter = (counter: number, countersMaxNumber?: number | null, isError?: boolean) => (
  <StyledCounter>
    <Counter
      isError={isError}
      counter={counter}
      maxNumber={typeof countersMaxNumber === 'number' ? countersMaxNumber : undefined}
    />
  </StyledCounter>
);

export function LinkWrapper({
  level,
  link: {
    id,
    title,
    renderIcon,
    payload: {
      url,
      target,
      isAnchor,
    },
  },

  isActiveItem,

  counters,
  countersMaxNumber = null,
  renderType,

  closeMenu,

  index,
  setHoverGroupIndex = undefined,
}: LinkWrapperProps): ReactElement {
  const onMouseEnter = useCallback(() => {
    if (setHoverGroupIndex) {
      setHoverGroupIndex(index);
    }
  }, [
    index,
    setHoverGroupIndex,
  ]);

  const isFirstLevel = level === 0;
  const counter = counters[id];

  const hasCounter = (typeof counter === 'number' && counter > 0) || (typeof counter === 'object' && counter.count > 0);
  const counterNum = typeof counter === 'number' ? counter : typeof counter === 'object' ? counter.count : 0;
  const counterError = typeof counter === 'object' && counter.isError;

  const isMenuVisible = renderType !== MENU_COLLAPSED;
  const isHoverRender = renderType === MENU_HOVER;

  return (
    <StyledWrapper
      isFirstLevel={isFirstLevel}
    >
      <StyledTitle
        isActiveItem={isActiveItem}
        hasPaddingRight={isMenuVisible}
        isHoverRender={isHoverRender}
        isFirstLevel={isFirstLevel}
        isAnchor={isAnchor || false}
        url={url}
        target={target}
        onClick={closeMenu}
        onMouseEnter={onMouseEnter}
      >
        {
          isFirstLevel && !isHoverRender && (
            <StyledTitleIcon
              $isMenuVisible={isMenuVisible}
              $isActive={isActiveItem}
            >
              {renderIcon && renderIcon()}
            </StyledTitleIcon>
          )
        }

        {
          isMenuVisible && (
            <StyledTitleText>
              {title}
            </StyledTitleText>
          )
        }

        {
          !isMenuVisible && hasCounter && (
            <StyledCounterPoint $isError={counterError} />
          )
        }

        {
          isMenuVisible && hasCounter && renderCounter(counterNum, countersMaxNumber, counterError)
        }
      </StyledTitle>
    </StyledWrapper>
  );
}
